import { CloseIcon, JMLogo, WarningIcon } from "@/constant/imagePath";
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";
import { downloadExcelFile, downloadZipFile } from "@/utils/ExportUtils";
import { useDelete, useGet, usePost, usePut } from "@/apiHooks/HttpFunctions";

import BaseModal from "../BaseModal";
import { CONFIRMATION_MODAL_TYPES } from "./constants";
import { HTTP_METHODS } from "@/constant/Http/methods";
import { cn } from "@/utils/cn";
import { useModalInfo } from "@/context/ModalContext";

const messages = {
  [CONFIRMATION_MODAL_TYPES.DELETE]:
    "Are you sure you want to delete this data?",
  [CONFIRMATION_MODAL_TYPES.CHANGE_STATUS]:
    "Are you sure you want to change the status of this data?",
  [CONFIRMATION_MODAL_TYPES.EXPORT]:
    "Are you sure you want to export this data?",
  [CONFIRMATION_MODAL_TYPES.EXPORT_ZIP]:
    "Are you sure you want to export this data?",
  [CONFIRMATION_MODAL_TYPES.MARK_UNREAD]:
    "Are you sure you want to change the status of this data to mark unread?",
  [CONFIRMATION_MODAL_TYPES.REOPEN_TICKET]:
    "Are you sure you want to reopen this ticket?",
  [CONFIRMATION_MODAL_TYPES.CHANGE_FEATURE_STATUS]:
    "Are you sure you want to change feature status of this data?",
};

const NewConfirmationModal = forwardRef((_, ref) => {
  const { openModalInfo } = useModalInfo();
  const [modalState, setModalState] = useState({
    isOpen: false,
    apiURL: null,
    id: null,
    type: null,
    title: null,
    customMutation: {
      confirmationMessage: null,
      apiURL: null,
      action: null,
      onSuccess: null,
      onError: null,
      data: {},
    },
  });
  const { isOpen, apiURL, id, type, title, customMutation } = modalState;

  const deleteApi = `${apiURL}/${id}`;
  const changeStatusApi = `${apiURL}/change-status/${id}`;
  const changeFeatureStatusApi = `${apiURL}/change-feature-status/${id}`;
  const markUnreadApi = `${apiURL}/${id}/mark-unread`;
  const reopenTicketApi = `${apiURL}/${id}/reopen`;

  const { mutate: processDeleteData, isPending: deleteIsPending } = useDelete({
    apiURL: deleteApi,
    onSuccess: () => {
      closeModal();
      openModalInfo("Data deleted successfully.");
    },
  });

  const { mutate: processMarkUnread, isPending: markUnreadIsPending } = usePut({
    apiURL: markUnreadApi,
    onSuccess: () => {
      closeModal();
      openModalInfo("Ticket updated successfully.");
    },
  });

  const { mutate: processChangeStatusData, isPending: changeStatusIsPending } =
    usePost({
      apiURL: changeStatusApi,
      onSuccess: () => {
        closeModal();
        openModalInfo("Data updated successfully.");
      },
    });

  const {
    mutate: processChangeFeatureStatusData,
    isPending: changeFeatureStatusIsPending,
  } = usePost({
    apiURL: changeFeatureStatusApi,
    onSuccess: () => {
      closeModal();
      openModalInfo("Data updated successfully.");
    },
  });

  //  FOR CONTACT SUPPORT TICKET
  const { mutate: processReopenTicket, isPending: reopenTicketIsPending } =
    usePost({
      apiURL: reopenTicketApi,
      onSuccess: () => {
        closeModal();
        openModalInfo("Ticket opened successfully.");
      },
    });

  //  FOR CONTACT SUPPORT TICKET

  const { isFetching: exportIsFetching, refetch: exportRefetch } = useGet({
    enabled: false,
    apiURL: `${apiURL}/export`,
    responseType: "blob",
  });

  const { isFetching: exportZipIsFetching, refetch: exportZipRefetch } = useGet(
    {
      enabled: false,
      apiURL: `${apiURL}/export-zip`,
      responseType: "blob",
    }
  );

  const putMutation = usePut({
    apiURL: customMutation?.apiURL,
    onSuccess: customMutation?.onSuccess,
    onError: customMutation?.onError,
    onSettled: () => {
      closeModal();
    },
  });
  const postMutation = usePut({
    apiURL: customMutation?.apiURL,
    onSuccess: customMutation?.onSuccess,
    onError: customMutation?.onError,
    onSettled: () => {
      closeModal();
    },
  });

  const isFetching =
    deleteIsPending ||
    changeStatusIsPending ||
    changeFeatureStatusIsPending ||
    markUnreadIsPending ||
    reopenTicketIsPending ||
    exportIsFetching ||
    exportZipIsFetching ||
    putMutation.isPending;

  const closeModal = useCallback(() => {
    setModalState({
      isOpen: false,
      apiURL: null,
      id: null,
      type: null,
      title: null,
      customMutation: null,
    });
  }, []);

  const onConfirm = useCallback(async () => {
    if (customMutation?.action === HTTP_METHODS.POST) {
      postMutation.mutate();
      return;
    } else if (customMutation?.action === HTTP_METHODS.PUT) {
      putMutation.mutate(customMutation?.data);
      return;
    }

    if (type === CONFIRMATION_MODAL_TYPES.DELETE) {
      processDeleteData();
    } else if (type === CONFIRMATION_MODAL_TYPES.REOPEN_TICKET) {
      processReopenTicket();
    } else if (type === CONFIRMATION_MODAL_TYPES.MARK_UNREAD) {
      processMarkUnread();
    } else if (type === CONFIRMATION_MODAL_TYPES.CHANGE_STATUS) {
      processChangeStatusData();
    } else if (type === CONFIRMATION_MODAL_TYPES.CHANGE_FEATURE_STATUS) {
      processChangeFeatureStatusData();
    } else if (type === CONFIRMATION_MODAL_TYPES.EXPORT) {
      const { data } = await exportRefetch();
      if (data) {
        downloadExcelFile(data, title);
        closeModal();
        openModalInfo("Data exported and downloaded successfully.");
      }
    } else if (type === CONFIRMATION_MODAL_TYPES.EXPORT_ZIP) {
      const { data } = await exportZipRefetch();
      if (data) {
        downloadZipFile(data, title);
        closeModal();
        openModalInfo("Data exported and downloaded successfully.");
      }
    }
  }, [
    type,
    processDeleteData,
    processChangeStatusData,
    processMarkUnread,
    processReopenTicket,
  ]);

  useImperativeHandle(ref, () => ({
    open({ type, title, id, apiURL, customMutation }) {
      setModalState({
        isOpen: true,
        type,
        title,
        id,
        apiURL,
        customMutation,
      });
    },
    close: closeModal,
  }));

  return (
    <BaseModal
      isOpen={isOpen}
      isFetching={isFetching}
      onClose={closeModal}
      width="520px"
      height="261px"
    >
      <div className="flex flex-col h-full w-full">
        <div className="flex justify-between items-center w-full">
          <img
            src={JMLogo}
            alt="JMLogo"
            className={cn("transition-all duration-300 w-10 h-5")}
          />
          <button
            disabled={isFetching}
            className="disabled:opacity-50 active:opacity-80"
            onClick={closeModal}
          >
            <img src={CloseIcon} alt="Close" />
          </button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center gap-y-5">
          <img src={WarningIcon} alt="Warning" />
          <div>
            <p className="font-light text-center">
              {customMutation?.action
                ? customMutation?.confirmationMessage
                : messages[type]}
            </p>
            {title && <p className="font-semibold text-center">{title}</p>}
          </div>
        </div>
        <div className="flex items-center gap-x-4 h-[38px]">
          <button
            disabled={isFetching}
            onClick={closeModal}
            className="w-full h-full rounded-7 font-light border disabled:bg-gray-100 active:bg-gray-50"
          >
            Cancel
          </button>
          <button
            disabled={isFetching}
            onClick={onConfirm}
            className="w-full h-full rounded-7 font-light bg-green-black-gradient text-white disabled:opacity-70 active:opacity-90"
          >
            Confirm
          </button>
        </div>
      </div>
    </BaseModal>
  );
});

export default NewConfirmationModal;
